import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { ROUTE_PATHS } from 'constants/index'

const HOW_IT_WORKS_STEPS = [
  {
    id: 'pick',
    title: 'pickAMarket',
    description: 'pickAMarketDescription',
    visual: 'pick'
  },
  {
    id: 'limit',
    title: 'preferLimitOrders',
    description: 'preferLimitOrdersDescription',
    visual: 'limit'
  },
  {
    id: 'split',
    title: 'noFillUseSplit',
    description: 'noFillUseSplitDescription',
    visual: 'split'
  },
  {
    id: 'market',
    title: 'needSpeedMarketOrders',
    description: 'needSpeedMarketOrdersDescription',
    visual: 'market'
  },
  {
    id: 'win',
    title: 'holdOrCashOut',
    description: 'holdOrCashOutDescription',
    visual: 'win'
  }
]

const illusCardClass =
  'relative z-[1] w-full max-w-[280px] rounded-2xl border border-line bg-surface-raised p-3.5 text-ink shadow-card'
const illusOrderCardClass = `${illusCardClass} flex flex-col gap-2`
const illusTabsClass = 'grid grid-cols-3 gap-1 rounded-[10px] bg-surface-muted p-0.5'
const illusSideClass = 'grid grid-cols-2 gap-1 rounded-[10px] bg-surface-muted p-0.5'
const illusTabItemClass =
  'inline-flex min-h-[26px] items-center justify-center rounded-lg text-[11px] font-bold text-ink-muted'
const illusTabActiveClass = `${illusTabItemClass} bg-gold-btn text-ink-inverse`
const illusFieldClass =
  'flex min-h-[34px] items-center justify-between gap-2 rounded-[10px] bg-surface-muted px-2.5 text-xs font-semibold text-ink-muted'
const illusCtaBase =
  'min-h-9 cursor-default rounded-[10px] border-none font-inherit text-[13px] font-bold pointer-events-none'
const illusCtaYes = `${illusCtaBase} bg-gold-btn text-ink-inverse shadow-gold`
const illusCtaNo = `${illusCtaBase} bg-surface-muted text-ink`
const illusCtaSell = `${illusCtaBase} w-full mt-0.5 bg-gold-dark text-ink`
const illusCtaOrderYes = `${illusCtaBase} w-full mt-0.5 bg-gold-btn text-ink-inverse`
const illusNoteClass = 'm-0 text-center text-[11px] leading-snug text-ink-faint'

const StepVisual = ({ type }) => {
  const { t } = useTranslation()

  if (type === 'limit') {
    return (
      <div
        className='absolute inset-0 flex animate-how-it-works-slide-in items-center justify-center px-6 pb-[18px] pt-7'
        aria-hidden='true'
      >
        <div className={illusOrderCardClass}>
          <div className={illusTabsClass}>
            <span className={illusTabItemClass}>{t('market')}</span>
            <span className={illusTabActiveClass}>{t('limit')}</span>
            <span className={illusTabItemClass}>{t('split')}</span>
          </div>
          <div className={illusSideClass}>
            <span className={illusTabActiveClass}>{t('buy')}</span>
            <span className={illusTabItemClass}>{t('sell')}</span>
          </div>
          <div className={illusFieldClass}>
            <span>{t('limitPrice')}</span>
            <strong className='text-[13px] text-ink'>48$</strong>
          </div>
          <div className={illusFieldClass}>
            <span>{t('shares')}</span>
            <strong className='text-[13px] text-ink'>50</strong>
          </div>
          <button type='button' className={illusCtaOrderYes}>
            {t('buyYes')}
          </button>
          <p className={illusNoteClass}>{t('restsOnTheBookUntilMatched')}</p>
        </div>
      </div>
    )
  }

  if (type === 'split') {
    return (
      <div
        className='absolute inset-0 flex animate-how-it-works-slide-in items-center justify-center px-6 pb-[18px] pt-7'
        aria-hidden='true'
      >
        <div className={illusOrderCardClass}>
          <div className={illusTabsClass}>
            <span className={illusTabItemClass}>{t('market')}</span>
            <span className={illusTabItemClass}>{t('limit')}</span>
            <span className={illusTabActiveClass}>{t('split')}</span>
          </div>
          <p className='m-0 text-left text-[11px] leading-snug text-ink-muted'>
            {t('convertCashIntoYesAndNoShares')}
          </p>
          <div className={illusFieldClass}>
            <span>{t('amount')}</span>
            <strong className='text-[13px] text-ink'>$25</strong>
          </div>
          <div className='grid grid-cols-2 gap-2'>
            <div className='flex flex-col items-center gap-0.5 rounded-[10px] bg-surface-muted px-1.5 py-2'>
              <em className='not-italic text-[11px] font-bold text-gold'>{t('yes')}</em>
              <strong className='text-base text-ink'>25</strong>
            </div>
            <div className='flex flex-col items-center gap-0.5 rounded-[10px] bg-surface-muted px-1.5 py-2'>
              <em className='not-italic text-[11px] font-bold text-ink-muted'>{t('no')}</em>
              <strong className='text-base text-ink'>25</strong>
            </div>
          </div>
          <button type='button' className={illusCtaSell}>
            {t('split')}
          </button>
        </div>
      </div>
    )
  }

  if (type === 'market') {
    return (
      <div
        className='absolute inset-0 flex animate-how-it-works-slide-in items-center justify-center px-6 pb-[18px] pt-7'
        aria-hidden='true'
      >
        <div className={illusOrderCardClass}>
          <div className={illusTabsClass}>
            <span className={illusTabActiveClass}>{t('market')}</span>
            <span className={illusTabItemClass}>{t('limit')}</span>
            <span className={illusTabItemClass}>{t('split')}</span>
          </div>
          <div className={illusSideClass}>
            <span className={illusTabActiveClass}>{t('buy')}</span>
            <span className={illusTabItemClass}>{t('sell')}</span>
          </div>
          <div className={illusFieldClass}>
            <span>{t('amount')}</span>
            <strong className='text-[13px] text-ink'>$25.00</strong>
          </div>
          <div className={`${illusFieldClass} opacity-80`}>
            <span>{t('estShares')}</span>
            <strong className='text-[13px] text-ink'>~48</strong>
          </div>
          <button type='button' className={illusCtaOrderYes}>
            {t('buyYes')}
          </button>
          <p className={illusNoteClass}>{t('fillsInstantlyAtBookPrices')}</p>
        </div>
      </div>
    )
  }

  if (type === 'win') {
    return (
      <div
        className='absolute inset-0 flex animate-how-it-works-slide-in items-center justify-center px-6 pb-[18px] pt-7'
        aria-hidden='true'
      >
        <div className={`${illusOrderCardClass} items-center gap-2.5 px-4 py-[22px] text-center`}>
          <div className='inline-flex min-h-[26px] items-center rounded-full bg-accent-soft px-2.5 text-xs font-bold text-gold'>
            {t('resolvedYes')}
          </div>
          <strong className='text-[30px] font-extrabold leading-tight text-gold'>$64.00</strong>
          <span className='text-[13px] text-ink-muted'>{t('payoutCredited')}</span>
        </div>
      </div>
    )
  }

  return (
    <div
      className='absolute inset-0 flex animate-how-it-works-slide-in items-center justify-center px-6 pb-[18px] pt-7'
      aria-hidden='true'
    >
      <div className='absolute left-[8%] top-[34px] h-[118px] w-[58%] rotate-[-8deg] scale-[0.92] rounded-2xl border border-line bg-surface-overlay opacity-55' />
      <div className='absolute right-[8%] top-[38px] h-[118px] w-[58%] rotate-[8deg] scale-[0.92] rounded-2xl border border-line bg-surface-overlay opacity-55' />
      <div className={illusCardClass}>
        <div className='flex items-start gap-2.5'>
          <p className='m-0 flex-1 text-sm font-bold leading-snug'>
            {t('willThereBeAUsRecessionThisYear')}
          </p>
          <div className='relative h-11 w-11 shrink-0'>
            <svg viewBox='0 0 36 36' className='h-11 w-11 -rotate-90'>
              <circle
                cx='18'
                cy='18'
                r='15.5'
                fill='none'
                className='stroke-line'
                strokeWidth='3.5'
              />
              <circle
                cx='18'
                cy='18'
                r='15.5'
                fill='none'
                className='stroke-gold'
                strokeWidth='3.5'
                strokeLinecap='round'
                strokeDasharray='48.7 97.4'
              />
            </svg>
            <span className='absolute inset-0 grid place-items-center text-[11px] font-bold'>
              50%
            </span>
          </div>
        </div>
        <div className='mt-3 grid grid-cols-2 gap-2'>
          <button type='button' className={illusCtaYes}>{t('yes')}</button>
          <button type='button' className={illusCtaNo}>{t('no')}</button>
        </div>
      </div>
    </div>
  )
}

const HowItWorks = ({ open = false, onClose = () => {} }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const [stepIndex, setStepIndex] = useState(0)
  const step = HOW_IT_WORKS_STEPS[stepIndex]
  const isLastStep = stepIndex === HOW_IT_WORKS_STEPS.length - 1

  useEffect(() => {
    if (!open) return undefined

    setStepIndex(0)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose])

  if (!open) return null

  const handleNext = () => {
    if (isLastStep) {
      onClose()
      return
    }
    setStepIndex((current) => Math.min(current + 1, HOW_IT_WORKS_STEPS.length - 1))
  }

  const handleTryItOut = () => {
    onClose()
    history.push(`${ROUTE_PATHS.predictionMarketMarketDetail}?isDemo=true&marketId=0`)
  }

  return createPortal(
    <div
      className='fixed inset-0 z-[1400] flex animate-pm-fade-in-fast items-center justify-center bg-[rgb(var(--color-shadow)/0.55)] p-4'
      role='presentation'
      onClick={onClose}
    >
      <div
        className='relative w-full max-w-[420px] animate-pm-scale-in overflow-hidden rounded-3xl border border-line-strong bg-surface-raised shadow-[0_24px_60px_rgb(var(--color-shadow)/0.28)] max-[700px]:rounded-[20px]'
        role='dialog'
        aria-modal='true'
        aria-labelledby='how-it-works-title'
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type='button'
          className='absolute right-3 top-3 z-[2] inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-none bg-surface-muted/85 text-ink-muted transition-[background,color] duration-200 hover:bg-surface-overlay hover:text-ink'
          onClick={onClose}
          aria-label={t('closeHowItWorks')}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 24 24'
            fill='currentColor'
            className='w-5 h-5'
            aria-hidden='true'
          >
            <path d='M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.41 4.29 19.71 2.88 18.3 9.17 12 2.88 5.71 4.29 4.29l6.3 6.3 6.3-6.3z' />
          </svg>
        </button>

        <div
          className='relative h-[248px] overflow-hidden bg-[radial-gradient(circle_at_50%_20%,rgb(var(--color-info-soft)/0.55),transparent_55%),linear-gradient(180deg,rgb(var(--color-surface-muted))_0%,rgb(var(--color-surface-raised))_100%)] max-[700px]:h-[220px]'
          key={step.id}
        >
          <StepVisual type={step.visual} />
          <div className='pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-b from-transparent to-surface-raised' />
        </div>

        <div className='px-6 pb-6 pt-2 max-[700px]:px-[18px] max-[700px]:pb-[18px] max-[700px]:pt-1.5'>
          <div className='mb-3.5 flex items-center gap-1.5' aria-hidden='true'>
            {HOW_IT_WORKS_STEPS.map((item, index) => (
              <span
                key={item.id}
                className={
                  index === stepIndex
                    ? 'h-[7px] w-[18px] rounded-full bg-info transition-[width,background] duration-200'
                    : 'h-[7px] w-[7px] rounded-full bg-line-strong transition-[width,background] duration-200'
                }
              />
            ))}
          </div>

          <h2
            id='how-it-works-title'
            className='m-0 text-[1.35rem] font-bold leading-tight tracking-tight text-ink max-[700px]:text-[1.2rem]'
          >
            {stepIndex + 1}. {t(step.title)}
          </h2>
          <p className='mt-2.5 min-h-[5.2em] text-[0.95rem] leading-normal text-ink-muted max-[700px]:min-h-[4.2em] max-[700px]:text-[0.9rem]'>
            {t(step.description)}
          </p>

          <button
            type='button'
            className='mt-5 min-h-12 w-full cursor-pointer rounded-lg border-none bg-gold-btn font-inherit text-base font-bold text-ink-inverse shadow-gold transition-[opacity,transform] duration-200 hover:opacity-[0.94] active:translate-y-px'
            onClick={handleNext}
          >
            {isLastStep ? t('gotIt') : t('next')}
          </button>
          <button
            type='button'
            className='mt-3 min-h-12 w-full cursor-pointer rounded-lg border border-line-strong bg-surface-muted font-inherit text-base font-bold text-ink transition-[opacity,transform,background] duration-200 hover:bg-surface-overlay active:translate-y-px'
            onClick={handleTryItOut}
          >
            {t('tryItOut')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default HowItWorks
